import { useCallback } from 'react';
import { apiService } from '../services/api';
import {
  dispatchVideoGeneration,
  pollVideoGenerationStatus,
} from '../services/videoGeneration';
import type { VideoGenerateRequest } from '../types/video';
import { useGenerationJobs } from './useGenerationJobs';

/**
 * Job state for the async video generation flow. No longer block on the
 * POST response — after dispatch the job is "pending" and this hook polls
 * the status endpoint until done/error/timeout. Mirrors
 * useCarouselGeneration.ts exactly, including per-post state.
 */
export type VideoGenerationState =
  | { status: 'idle' }
  | { status: 'pending'; postId: string }
  | { status: 'done'; postId: string; videoUrl: string }
  | { status: 'error'; postId: string; errorMessage: string }
  | { status: 'timeout'; postId: string };

export interface VideoGenerationCallbacks {
  onDone?: (
    postId: string,
    videoUrl: string,
    meta?: { alreadyGenerated?: boolean }
  ) => void | Promise<void>;
  onError?: (postId: string, message: string) => void | Promise<void>;
  onTimeout?: (postId: string) => void | Promise<void>;
}

const IDLE: VideoGenerationState = { status: 'idle' };

export function useVideoGeneration() {
  const { jobs, setJob, stopPolling, trackPolling, clear } = useGenerationJobs<VideoGenerationState>();

  // Polls pollPostId's job, recording its state under the editor's postId.
  const poll = useCallback(
    (postId: string, pollPostId: string, callbacks: VideoGenerationCallbacks) => {
      const stop = pollVideoGenerationStatus(apiService, {
        postId: pollPostId,
        onDone: async (videoUrl) => {
          setJob(postId, { status: 'done', postId: pollPostId, videoUrl });
          await callbacks.onDone?.(pollPostId, videoUrl);
        },
        onError: async (message) => {
          setJob(postId, { status: 'error', postId: pollPostId, errorMessage: message });
          await callbacks.onError?.(pollPostId, message);
        },
        onTimeout: async () => {
          setJob(postId, { status: 'timeout', postId: pollPostId });
          await callbacks.onTimeout?.(pollPostId);
        },
      });
      trackPolling(postId, stop);
    },
    [setJob, trackPolling]
  );

  const start = useCallback(
    async (request: VideoGenerateRequest, callbacks: VideoGenerationCallbacks = {}) => {
      const postId = request.post_id;
      // Replaces only this post's previous job — other posts keep generating.
      stopPolling(postId);
      setJob(postId, { status: 'pending', postId });

      let dispatched;
      try {
        dispatched = await dispatchVideoGeneration(apiService, request);
      } catch (err: any) {
        const message = err?.message || 'Could not start generation. Please try again.';
        setJob(postId, { status: 'error', postId, errorMessage: message });
        await callbacks.onError?.(postId, message);
        return;
      }

      // Guard: the post already has a video and force_regenerate wasn't set.
      const alreadyGenerated =
        dispatched.response &&
        'error_code' in dispatched.response &&
        dispatched.response.error_code === 'ALREADY_GENERATED';
      if (alreadyGenerated) {
        const existing = dispatched.response as import('../types/video').VideoAlreadyGeneratedResponse;
        if (existing.output_path) {
          setJob(postId, { status: 'done', postId, videoUrl: existing.output_path });
          await callbacks.onDone?.(postId, existing.output_path, { alreadyGenerated: true });
        } else {
          const message = existing.message || 'This post already has a generated video.';
          setJob(postId, { status: 'error', postId, errorMessage: message });
          await callbacks.onError?.(postId, message);
        }
        return;
      }

      poll(postId, dispatched.postId || postId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const retry = useCallback(
    async (request: VideoGenerateRequest, callbacks: VideoGenerationCallbacks = {}) => {
      // Explicit user action — force a fresh generation so a still-running
      // previous job (e.g. the timed-out one) can't trip ALREADY_GENERATED.
      await start({ ...request, force_regenerate: true }, callbacks);
    },
    [start]
  );

  // Resumes polling for a job that's already running server-side (e.g. the
  // user refreshed the page mid-generation) without dispatching a new one.
  const resume = useCallback(
    (postId: string, callbacks: VideoGenerationCallbacks = {}) => {
      stopPolling(postId);
      setJob(postId, { status: 'pending', postId });
      poll(postId, postId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const stateFor = (postId?: string | null): VideoGenerationState => (postId && jobs[postId]) || IDLE;

  return {
    stateFor,
    isPendingFor: (postId?: string | null) => stateFor(postId).status === 'pending',
    isErrorFor: (postId?: string | null) => stateFor(postId).status === 'error',
    isTimedOutFor: (postId?: string | null) => stateFor(postId).status === 'timeout',
    errorMessageFor: (postId?: string | null) => {
      const state = stateFor(postId);
      return state.status === 'error' ? state.errorMessage : null;
    },
    start,
    retry,
    resume,
    clear,
  };
}
